import { parse, stringify } from 'devalue';
import { isRecord } from '../utils/type-guards';

const DEVALUE_PAYLOAD_KEY = '__devalue__payload';

export type SerializedActionPayload = {
  [DEVALUE_PAYLOAD_KEY]: string;
};

export function isSerializedActionPayload(value: unknown): value is SerializedActionPayload {
  if (!isRecord(value)) {
    return false;
  }
  return typeof value[DEVALUE_PAYLOAD_KEY] === 'string';
}

export function serializeActionData<T>(data: T): SerializedActionPayload {
  if (isSerializedActionPayload(data)) {
    return data;
  }
  // Turbopack/Next JSON-path always stringifies the response body; devalue preserves
  // native constructs (Date, Map, undefined, bigint …) by encoding them into a string
  // that can be revived on the client. We wrap every payload so the client can always
  // deserialize without guessing whether the server touched a complex type.
  return {
    [DEVALUE_PAYLOAD_KEY]: stringify(data),
  };
}

export function deserializeActionData<T>(data: T | SerializedActionPayload): T {
  if (!isSerializedActionPayload(data)) {
    return data;
  }
  // The client receives the same encoded payload; parse revives the original object graph.
  const encoded = data[DEVALUE_PAYLOAD_KEY];
  if (typeof encoded !== 'string') {
    throw new TypeError('Serialized action payload missing encoded string');
  }
  const decoded: unknown = parse(encoded);
  // We intentionally trust the encoded payload to match the server type T.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- devalue parse returns unknown; callers supply T.
  return decoded as T;
}

export function serializeSafeActionResult<T extends { data?: unknown }>(result: T): T {
  if (!result || typeof result !== 'object') {
    return result;
  }

  if (!('data' in result)) {
    return result;
  }

  // Preserve the envelope (validation/serverError) but transform the data payload
  // so callers receive the exact types produced on the server.
  return {
    ...result,
    data: serializeActionData(result.data),
  };
}

export function deserializeSafeActionResult<T extends { data?: unknown }>(result: T): T {
  if (!result || typeof result !== 'object') {
    return result;
  }

  if (!('data' in result)) {
    return result;
  }

  // Matching the server side: unwrap the encoded data so downstream code doesn't
  // have to care whether the safe action crossed the JSON transport boundary.
  return {
    ...result,
    data: deserializeActionData(result.data),
  };
}
